import React, { useState } from "react";
import styled from "styled-components";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { OTAComponentVersion } from "../types";
import { otaVersionsApi } from "../api";

interface Props {
  version: OTAComponentVersion;
  componentType: string;
  onClose: () => void;
  onVersionUpdated?: (updated: OTAComponentVersion) => void;
}

export const VersionDetailsModal: React.FC<Props> = ({
  version,
  componentType,
  onClose,
  onVersionUpdated,
}) => {
  const type = componentType.toLowerCase();
  const isContainer = type === "container";
  const isHost = type === "host";
  const isFirmware = !isContainer && !isHost;

  const [description, setDescription] = useState(version.description ?? "");
  const [tagsInput, setTagsInput] = useState((version.tags ?? []).join(", "));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const tags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const updated = await otaVersionsApi.update(version.id, {
        description: description || null,
        tags,
      });
      toast.success("Version updated");
      onVersionUpdated?.(updated);
    } catch {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <div>
            <Title>
              {version.componentName}{" "}
              <Version>v{version.versionNumber}</Version>
            </Title>
            <TypeBadge $type={type}>{componentType}</TypeBadge>
          </div>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>

        <Body>
          {version.isDeprecated && (
            <DeprecatedBanner>This version is deprecated</DeprecatedBanner>
          )}

          <Section>
            <SectionTitle>Identification</SectionTitle>
            <Row>
              <FieldLabel>Version</FieldLabel>
              <FieldValue>{version.versionNumber}</FieldValue>
            </Row>
            <Row>
              <FieldLabel>AWS Component</FieldLabel>
              <FieldValue>
                <code>{version.awsComponentName || "—"}</code>
              </FieldValue>
            </Row>
            {version.awsComponentArn && (
              <Row>
                <FieldLabel>ARN</FieldLabel>
                <FieldValue>
                  <code style={{ wordBreak: "break-all", fontSize: 11 }}>
                    {version.awsComponentArn}
                  </code>
                </FieldValue>
              </Row>
            )}
          </Section>

          {isContainer && (
            <Section>
              <SectionTitle>CCM Configuration</SectionTitle>
              <Row>
                <FieldLabel>Image Tag</FieldLabel>
                <FieldValue>
                  <code>{version.ccmImageTag || "—"}</code>
                </FieldValue>
              </Row>
              {version.ccmSourceName && (
                <Row>
                  <FieldLabel>Source Name</FieldLabel>
                  <FieldValue>
                    <code>{version.ccmSourceName}</code>
                  </FieldValue>
                </Row>
              )}
            </Section>
          )}

          {isHost && (
            <Section>
              <SectionTitle>Host Package</SectionTitle>
              <Row>
                <FieldLabel>Package Version</FieldLabel>
                <FieldValue>
                  <code>{version.hostPackageVersion || "—"}</code>
                </FieldValue>
              </Row>
            </Section>
          )}

          {isFirmware && (
            <Section>
              <SectionTitle>Firmware</SectionTitle>
              <Row>
                <FieldLabel>Firmware Version</FieldLabel>
                <FieldValue>
                  <code>{version.firmwareVersion || "—"}</code>
                </FieldValue>
              </Row>
              <Row>
                <FieldLabel>S3 URI</FieldLabel>
                <FieldValue>
                  <code style={{ wordBreak: "break-all", fontSize: 11 }}>
                    {version.s3Uri || "—"}
                  </code>
                </FieldValue>
              </Row>
              <Row>
                <FieldLabel>SHA-256</FieldLabel>
                <FieldValue>
                  <code
                    style={{
                      wordBreak: "break-all",
                      fontSize: 11,
                      color: "#059669",
                    }}
                  >
                    {version.sha256Hash || "—"}
                  </code>
                </FieldValue>
              </Row>
            </Section>
          )}

          <Section>
            <SectionTitle>Timestamps</SectionTitle>
            <Row>
              <FieldLabel>Created</FieldLabel>
              <FieldValue>
                {format(new Date(version.createdAt), "MMM dd, yyyy HH:mm")}
              </FieldValue>
            </Row>
            <Row>
              <FieldLabel>Updated</FieldLabel>
              <FieldValue>
                {format(new Date(version.updatedAt), "MMM dd, yyyy HH:mm")}
              </FieldValue>
            </Row>
          </Section>

          <Section>
            <SectionTitle>Notes &amp; Tags</SectionTitle>
            <EditLabel>Description</EditLabel>
            <EditTextarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description (e.g. 'June sprint — stable baseline')"
              rows={2}
              disabled={saving}
            />
            <EditLabel style={{ marginTop: 8 }}>Tags</EditLabel>
            <EditInput
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Comma-separated (e.g. stable, june-sprint, rc1)"
              disabled={saving}
            />
          </Section>
        </Body>

        <Footer>
          <CloseButton onClick={onClose} disabled={saving}>
            Close
          </CloseButton>
          <SaveButton onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </SaveButton>
        </Footer>
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 560px;
  max-width: 92vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.16);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
`;

const Title = styled.h2`
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 600;
  color: #111827;
`;

const Version = styled.span`
  color: #0369a1;
`;

const TypeBadge = styled.span<{ $type: string }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ $type }) =>
    $type === "container"
      ? "#dbeafe"
      : $type === "host"
      ? "#d1fae5"
      : "#fef3c7"};
  color: ${({ $type }) =>
    $type === "container"
      ? "#1e40af"
      : $type === "host"
      ? "#065f46"
      : "#92400e"};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 26px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    color: #111827;
  }
`;

const Body = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DeprecatedBanner = styled.div`
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 6px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #9ca3af;
  padding-bottom: 4px;
  border-bottom: 1px solid #f3f4f6;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 8px;
  align-items: baseline;
`;

const FieldLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  flex-shrink: 0;
`;

const FieldValue = styled.span`
  font-size: 13px;
  color: #111827;

  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 12px;
  }
`;

const Footer = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.button`
  padding: 8px 22px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  padding: 8px 22px;
  background: #55b8b3;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #3d9e99;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  display: block;
  margin-bottom: 4px;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #55b8b3;
    box-shadow: 0 0 0 2px rgba(85, 184, 179, 0.15);
  }

  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }
`;

const EditInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #55b8b3;
    box-shadow: 0 0 0 2px rgba(85, 184, 179, 0.15);
  }

  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }
`;
